import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Skeleton,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import Slider from 'react-slick';

import { getUrl } from '../features/ddjj/decJuradaSlice';
import {
  getImponiblesByCuit,
  getImponiblesList,
  statusImponible } from '../features/imponibles/imponiblesSlice';
import {
  getLeftDashboard,
  getLeftDashboards,
  getRightDashboard,
  getRightDashboards,
} from '../features/dashboard/dashboardSlice';
import { cleanCuotasState } from '../features/cuentacorriente/ctaCorrienteSlice';
import { cleanPlanState } from '../features/planes/planesSlice';
import { store } from '../store';

import Imponible from '../components/Imponible';
import AddImponible from '../components/AddImponible';
import PreguntasFrecuentesSection from '../components/PreguntasFrecuentes';
import ExpirationList from '../components/ExpirationList';
import Page from '../components/Page';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './Home.css';

import homeCalendar from '../assets/home-calendar.png';

const Section = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  padding: 0,
  marginTop: '1rem',
  paddingBottom: '1.875rem',

  [theme.breakpoints.up('sm')]: {
    marginTop: 'unset',
    padding: '1.875rem',
  },
}));

const WelcomeWrapper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  alignItems: 'center',
  padding: '1rem 2rem 1rem 1.5rem',
  justifyContent: 'space-between',

  [theme.breakpoints.down('sm')]: {
    marginTop: '1rem',
  },
}));

const NewsWrapper = styled(Paper)(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '2rem',
}));


const SliderWrapper = styled(Box)(() => ({
  width: '100%',
}));

const CustomButton = styled(Button)(() => ({
  marginLeft: 10,
  padding: '.4rem 2rem',
  fontSize: '.75rem',
  fontWeight: '700',
}));

const GridImponibles = styled(Grid)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  padding: '1rem',

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

const settings = {
  dots: true,
  arrows: false,
  infinite: false,
  speed: 500,
  slidesToShow: 6,
  slidesToScroll: 6,
  className: 'center',
  swipe: true,
  initialSlide: 0,
  rows: 1,
  responsive: [
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 4,
        slidesToScroll: 4,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 3,
        dots: false,
        arrows: true,
      },
    },
    {
      breakpoint: 576,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: true,
        centerPadding: '20px',
        dots: false,
        arrows: true,
      },
    },
  ],
};


const getVideoId = (enlace: string) => {
  let idVideo = '';
  if (enlace.includes('youtube.com/watch?v=')) {
    idVideo = enlace.split('youtube.com/watch?v=')[1];
  } else if (enlace.includes('youtu.be/')) {
    idVideo = enlace.split('youtu.be/')[1];
  }

  if (idVideo !== '') {
    return idVideo;
  } else {
    throw new Error('El enlace de YouTube proporcionado no es válido');
  }
};

export default function Home() {
  const theme = useTheme();

  const desktop = useMediaQuery(theme.breakpoints.up('md'));

  const sliderRef = useRef<Slider>(null);

  const [openModal, setOpenModal] = useState(false);

  const imponibles = useSelector(getImponiblesList);
  const dashboardRight = useSelector(getRightDashboards);
  const dashboardLeft = useSelector(getLeftDashboards);

  const statusImponibles = useSelector(statusImponible);

  useEffect(() => {
    if (!dashboardRight) {
      store.dispatch(getRightDashboard());
    }

    if (!dashboardLeft) {
      store.dispatch(getLeftDashboard());
    }

    store.dispatch(getImponiblesByCuit())
      .unwrap()
      .then(() => {
        sliderRef.current?.slickGoTo(0);
      })
      .then(() => {
        store.dispatch(getUrl());
      });

    store.dispatch(cleanCuotasState());
    store.dispatch(cleanPlanState());
  }, []);

  return (
    <Page>
      <Section>
        <Container>
          <Grid
            container
            spacing={desktop ? '1.875rem' : '1rem'}
            direction={desktop ? 'row' : 'column'}
          >
            <Grid item sm={8}>
              <WelcomeWrapper>
                {!dashboardLeft ? (
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <>
                    {dashboardLeft && dashboardLeft.url_video ? (
                      <Grid container spacing="38px" direction="row">
                        <Grid
                          item
                          md={12}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <div className="video-responsive">
                            <iframe
                              width="1268"
                              height="713"
                              src={`https://www.youtube.com/embed/${getVideoId(
                                dashboardLeft.url_video
                              )}?loop=1&playlist=${getVideoId(
                                dashboardLeft.url_video
                              )}&controls=0&autoplay=1&mute=1&fs=0&rel=0&showinfo=0&modestbranding=1`}
                              title="MSM Video"
                              // eslint-disable-next-line max-len
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            ></iframe>
                          </div>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid container spacing="38px" direction="row">
                        <Grid
                          item
                          md={6}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="h1"
                            mb="1rem"
                            sx={{ textAlign: 'left' }}
                          >
                            {dashboardLeft ? dashboardLeft.texto : null}
                          </Typography>

                          <Typography variant="body2">
                            {dashboardLeft ? dashboardLeft.bajada : null}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          md={6}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {
                            dashboardLeft?.imagen
                              ? <img src={ dashboardLeft.imagen } style={{ maxWidth: '100%', height: 'auto' }} />
                              : null
                          }
                        </Grid>
                      </Grid>
                    )}
                  </>
                )}
              </WelcomeWrapper>
            </Grid>
            <Grid item sm={4}>
              <NewsWrapper>
                <img
                  src={dashboardRight ? dashboardRight.imagen : homeCalendar}
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
                <Typography variant="subtitle2" mt="2rem">
                  {dashboardRight ? dashboardRight.texto : null}
                </Typography>
              </NewsWrapper>
            </Grid>
          </Grid>
        </Container>
      </Section>
      <Container>
        <Grid container padding={'0 1.875rem'}>
          <Grid item xs={12} my={2} sx={{ alignItems: 'center', display: 'flex' }}>
            <Typography variant="subtitle1">Mis Imponibles</Typography>
            <CustomButton variant="outlined" onClick={() => setOpenModal(!openModal)}>
              + AGREGAR IMPONIBLE
            </CustomButton>
          </Grid>
          <GridImponibles item md={12}>
            <SliderWrapper id={'Imponibles'}>
              <Slider {...settings} ref={sliderRef}>
                {
                  statusImponibles === 'loading'
                    ? Array(settings.slidesToShow)
                      .fill('')
                      .map((_, i) => (
                        <Box key={i} className='slick-slide'>
                          <Skeleton height="15rem" sx={{ transform: 'none', width: '11rem' }} />
                        </Box>
                      ))
                    : imponibles
                      .filter(imponible => imponible.recursos.length > 0)
                      .map(imponible => <Imponible key={imponible.id} imponible={imponible} />)
                }
              </Slider>
            </SliderWrapper>
          </GridImponibles>
          {/* Modal */}
          <AddImponible openModal={openModal} setOpenModal={setOpenModal} />
        </Grid>
        <Grid container
          padding={'1.875rem'}
          spacing={desktop ? '1.875rem' : '1rem'}
          direction={desktop ? 'row' : 'column'}
        >
          <Grid item lg={8}>
            {
              imponibles.length > 0
                ? <ExpirationList />
                : null
            }
          </Grid>
          <PreguntasFrecuentesSection />
        </Grid>
      </Container>
    </Page>
  );
}
